import {
  Button,
  Chip,
  Input,
  Tooltip,
} from '@heroui/react'
import {
  AlertCircle,
  ExternalLink,
  Globe,
  Plus,
  X,
} from 'lucide-react'
import { useRef, useState } from 'react'

function isValidUrl(urlString) {
  try {
    // If URL doesn't start with protocol, prepend https://
    let normalizedUrl = urlString
    if (!urlString.match(/^https?:\/\//)) {
      normalizedUrl = `https://${urlString}`
    }

    const url = new URL(normalizedUrl)
    return url.protocol === 'http:' || url.protocol === 'https:'
  }
  catch {
    return false
  }
}

function truncateUrl(urlString, maxLength = 50) {
  if (urlString.length <= maxLength) {
    return urlString
  }

  try {
    const url = new URL(urlString)
    const domain = url.hostname
    const path = url.pathname + url.search

    // If domain + some path fits, show it
    if (domain.length + 10 < maxLength) {
      const availablePathLength = maxLength - domain.length - 10 // 10 for "https://" + "..."
      const truncatedPath = path.length > availablePathLength
        ? `${path.substring(0, availablePathLength)}...`
        : path
      return `${domain}${truncatedPath}`
    }
    else {
      // If domain is too long, truncate it
      return domain.length > maxLength - 3
        ? `${domain.substring(0, maxLength - 3)}...`
        : domain
    }
  }
  catch {
    // If URL parsing fails, just truncate the string
    return urlString.length > maxLength
      ? `${urlString.substring(0, maxLength - 3)}...`
      : urlString
  }
}

export default function URLChipInput({ urls, onChange, placeholder = 'Enter website URLs...' }) {
  const [inputValue, setInputValue] = useState('')
  const [invalidUrl, setInvalidUrl] = useState(false)
  const inputRef = useRef(null)

  const addUrl = () => {
    let trimmedUrl = inputValue.trim()
    if (!trimmedUrl)
      return

    // Auto-prepend https:// if no protocol provided
    if (!trimmedUrl.match(/^https?:\/\//)) {
      trimmedUrl = `https://${trimmedUrl}`
    }

    // Check if it's a valid URL
    if (!isValidUrl(trimmedUrl)) {
      setInvalidUrl(true)
      return
    }

    // Check if URL already exists
    if (urls.includes(trimmedUrl)) {
      setInputValue('')
      return
    }

    // Add the URL
    onChange([...urls, trimmedUrl])
    setInputValue('')
    setInvalidUrl(false)
  }

  const handleInputChange = (e) => {
    const value = e.target.value
    setInputValue(value)
    setInvalidUrl(false)
  }

  const handleInputKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addUrl()
    }
    else if (e.key === ' ' && inputValue.includes('http')) {
      e.preventDefault()
      addUrl()
    }
  }

  const removeUrl = (urlToRemove) => {
    onChange(urls.filter(url => url !== urlToRemove))
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')

    // Try to extract URLs from pasted text - both with and without protocols
    const urlWithProtocolPattern = /https?:\/\/\S+/g
    const urlWithoutProtocolPattern = /(?:^|\s)([a-z0-9-]+\.)+[a-z]{2,}(?:\/\S*)?/gi

    const urlsWithProtocol = pastedText.match(urlWithProtocolPattern) || []
    const urlsWithoutProtocol = pastedText.match(urlWithoutProtocolPattern) || []

    // Combine and normalize all URLs
    const allExtractedUrls = [
      ...urlsWithProtocol,
      ...urlsWithoutProtocol.map(url => url.trim()),
    ]

    if (allExtractedUrls.length > 0) {
      const validUrls = allExtractedUrls
        .map((url) => {
          // Auto-prepend https:// if no protocol provided
          if (!url.match(/^https?:\/\//)) {
            return `https://${url}`
          }
          return url
        })
        .filter(url => isValidUrl(url))
        .filter(url => !urls.includes(url))

      if (validUrls.length > 0) {
        onChange([...urls, ...validUrls])
      }
    }
    else {
      // If no URLs found, treat as regular input
      setInputValue(prev => prev + pastedText)
    }
  }

  return (
    <div className="space-y-3">
      {/* URL Chips Display */}
      {urls.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 border border-gray-200 min-h-[60px] max-h-32 overflow-y-auto">
          {urls.map((url, _index) => (
            <Tooltip key={`url-${url.replace(/[^a-z0-9]/gi, '-')}`} content={url} placement="top">
              <Chip
                variant="flat"
                color="primary"
                radius="sm"
                className="bg-blue-100 text-blue-800 border border-blue-200 text-sm font-medium cursor-pointer max-w-80"
                startContent={(
                  <div className="flex items-center gap-1">

                    <Globe className="w-3 h-3 text-blue-600" />
                  </div>
                )}
                endContent={(
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        window.open(url, '_blank')
                      }}
                      className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                      title="Open in new tab"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        removeUrl(url)
                      }}
                      className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                      title="Remove URL"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              >
                <span className="truncate" title={url}>
                  {truncateUrl(url, 45)}
                </span>
              </Chip>
            </Tooltip>
          ))}
        </div>
      )}

      {/* Input Field */}
      <div className="relative">
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyPress}
          onPaste={handlePaste}
          placeholder={urls.length > 0 ? 'Add another URL...' : placeholder}
          radius="none"
          classNames={{
            input: 'bg-gray-50',
            inputWrapper: `border-gray-200 ${invalidUrl ? 'border-red-500' : ''}`,
          }}
          startContent={(
            <div className="flex items-center gap-1 text-gray-500">
              <Globe className="w-4 h-4" />
            </div>
          )}
          endContent={
            inputValue.trim() && (
              <Button
                isIconOnly
                size="sm"
                variant="light"
                onPress={addUrl}
                className="min-w-6 w-6 h-6"
                radius="full"
              >
                <Plus className="w-3 h-3" />
              </Button>
            )
          }
        />

        {invalidUrl && (
          <div className="flex items-center gap-1 mt-1 text-red-600 text-xs">
            <AlertCircle className="w-3 h-3" />
            <span>Please enter a valid URL (https:// will be added automatically if omitted)</span>
          </div>
        )}
      </div>

      {/* Instructions */}
      {urls.length === 0
        ? (
            <div className="text-center py-3 text-gray-500">
              <p className="text-sm">Type or paste website URLs above</p>
              <p className="text-xs mt-1">Press Enter, comma, or space to add them</p>
            </div>
          )
        : (
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Hover chips to see full URLs</span>
              <span className="text-blue-600 font-medium">
                {urls.length}
                {' '}
                URL
                {urls.length !== 1 ? 's' : ''}
                {' '}
                added
              </span>
            </div>
          )}
    </div>
  )
}
